import React, { useMemo, useState } from 'react';
import { useI18n } from '../i18n';
import { fmtMoney, fmtLongDate, fmtMonth, monthKey } from '../lib/format';
import { IconSearch, IconBell, IconPlus } from './PortalIcons';
import SwipeToDelete from './SwipeToDelete';

// The technician portal's list screen, shared by Estimates, Invoices and
// Payments. Laid out like Joist: title row with a + on the right, a search
// field, a segmented filter, then rows grouped under month headings with the
// amount right-aligned above a status chip.
//
// The caller passes rows already shaped for display — this component owns the
// search box, the grouping and the empty states, so all three tabs behave
// identically without repeating any of it.
//
// A row is:
//   { id, name, subtitle, meta, date, amount, chipLabel, chipTone, onClick, onDelete }
//
// `onDelete` is optional. When a row has one, sliding it aside reveals Delete
// — the same gesture the estimate's own line items use, so there is one way
// to take something back in this portal rather than two.

export default function RecordList({
  title,
  rows,
  segments,            // [{ key, label, match: row => bool }] — omit for no filter
  segment, onSegment,
  searchPlaceholder,
  onAdd, addLabel,
  onBell, bellDot,
  totals,              // [[label, value], …] rendered above the list
  emptyTitle, emptyText,
}) {
  const { t, lang } = useI18n();
  const [q, setQ] = useState('');

  // Counts sit next to each segment label, so they're taken before the search
  // narrows anything — searching shouldn't make the tab counts jump around.
  const counts = useMemo(() => {
    const out = {};
    (segments || []).forEach(s => { out[s.key] = rows.filter(s.match).length; });
    return out;
  }, [rows, segments]);

  const visible = useMemo(() => {
    const seg = (segments || []).find(s => s.key === segment);
    let list = seg ? rows.filter(seg.match) : rows;
    const needle = q.trim().toLowerCase();
    if (needle) {
      list = list.filter(r =>
        [r.name, r.subtitle, r.meta, r.number, r.amount != null ? String(r.amount) : '']
          .filter(Boolean).join(' ').toLowerCase().includes(needle)
      );
    }
    return [...list].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [rows, segments, segment, q]);

  // Group into months, newest first. Rows are already sorted, so the first
  // time a month is seen is where its heading goes.
  const groups = useMemo(() => {
    const out = [];
    visible.forEach(r => {
      const key = monthKey(r.date);
      const last = out[out.length - 1];
      if (last && last.key === key) last.rows.push(r);
      else out.push({ key, date: r.date, rows: [r] });
    });
    return out;
  }, [visible]);

  return (
    <>
      <div className="tp-head">
        <div className="tp-head-row">
          {/* Keeps the title optically centered when there's no bell. */}
          <div style={{ width: 34, flexShrink: 0 }}>
            {onBell && (
              <button className="tp-act" onClick={onBell} aria-label={t('tp.alerts')} style={{ position: 'relative' }}>
                <IconBell size={21} />
                {bellDot && <span style={{ position: 'absolute', top: 5, right: 5, width: 7, height: 7, borderRadius: '50%', background: 'var(--red)' }} />}
              </button>
            )}
          </div>
          <div className="tp-title">{title}</div>
          <div style={{ width: 34, flexShrink: 0 }}>
            {onAdd && (
              <button className="tp-act tp-act-plus" onClick={onAdd} aria-label={addLabel || t('tp.new')}>
                <IconPlus size={23} />
              </button>
            )}
          </div>
        </div>

        <div className="tp-search">
          <IconSearch />
          <input
            type="search"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
          />
        </div>

        {segments && segments.length > 0 && (
          <div className="tp-seg" role="tablist">
            {segments.map(s => (
              <button
                key={s.key}
                role="tab"
                aria-selected={segment === s.key}
                className={segment === s.key ? 'active' : ''}
                onClick={() => onSegment(s.key)}
              >
                {s.label}
                {counts[s.key] > 0 && <span className="tp-seg-n">{counts[s.key]}</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {totals && totals.length > 0 && (
        <div className="tp-tot">
          {totals.map(([label, value]) => (
            <div key={label} className="tp-tot-cell">
              <div className="tp-tot-l">{label}</div>
              <div className="tp-tot-v">{fmtMoney(value)}</div>
            </div>
          ))}
        </div>
      )}

      {groups.length === 0 ? (
        <div className="tp-empty">
          <div className="tp-empty-t">{q.trim() ? t('tp.noMatches') : emptyTitle}</div>
          <div>{q.trim() ? t('tp.noMatchesSub') : emptyText}</div>
        </div>
      ) : groups.map(g => (
        <div key={g.key}>
          <div className="tp-month">{fmtMonth(g.date, lang)}</div>
          <div className="tp-rows">
            {g.rows.map(r => {
              const row = (
                <button className="tp-row" onClick={r.onClick} disabled={!r.onClick}
                  style={!r.onClick ? { cursor: 'default' } : undefined}>
                  <div className="tp-row-main">
                    <div className="tp-row-name">{r.name}</div>
                    {/* What the job IS, on its own line. It used to ride at
                        the end of the date line, where a phone cut it off
                        after the document number — so a list of estimates
                        read as a list of reference codes. */}
                    {r.subtitle && <div className="tp-row-sub">{r.subtitle}</div>}
                    <div className="tp-row-meta">
                      {fmtLongDate(r.date, lang)}
                      {r.number ? ` · ${r.number}` : ''}
                      {r.meta ? ` · ${r.meta}` : ''}
                    </div>
                  </div>
                  <div className="tp-row-right">
                    <div className="tp-row-amt">{fmtMoney(r.amount)}</div>
                    {r.chipLabel && <span className={`tp-chip tp-chip-${r.chipTone}`}>{r.chipLabel}</span>}
                  </div>
                </button>
              );
              return r.onDelete
                ? <SwipeToDelete key={r.id} onDelete={r.onDelete}>{row}</SwipeToDelete>
                : <React.Fragment key={r.id}>{row}</React.Fragment>;
            })}
          </div>
        </div>
      ))}
    </>
  );
}
